import { PROJECTS, PUBS, SKILLS, CONTACT, EXPERIENCE, EDUCATION, BLOGS } from './portfolio';
import type { ViewName } from '../types';

export type GraphKind = 'root' | 'branch' | 'item' | 'metric';

export type GraphAction =
  | { type: 'view'; view: ViewName }
  | { type: 'link'; href: string }
  | { type: 'project'; id: string };

export interface GraphNode {
  id: string;
  label: string;
  sub?: string;
  kind: GraphKind;
  children?: GraphNode[];
  action?: GraphAction;
}

export const GRAPH_ROOT: GraphNode = {
  id: 'me',
  label: 'me',
  sub: 'sahil chordia',
  kind: 'root',
  children: [
    {
      id: 'experience',
      label: 'experience',
      kind: 'branch',
      action: { type: 'view', view: 'experience' },
      children: EXPERIENCE.map((job) => ({
        id: `exp-${job.id}`,
        label: job.org.toLowerCase(),
        sub: job.title.toLowerCase(),
        kind: 'item' as const,
        children: (job.metrics || []).map(([v, k]) => ({
          id: `exp-${job.id}-${k}`,
          label: v,
          sub: k,
          kind: 'metric' as const,
        })),
      })),
    },
    {
      id: 'education',
      label: 'education',
      kind: 'branch',
      action: { type: 'view', view: 'education' },
      children: EDUCATION.map((edu) => ({
        id: `edu-${edu.id}`,
        label: edu.degree.toLowerCase(),
        sub: `${edu.school.split(',')[0].toLowerCase()} · ${edu.dates.replace(/ /g, '')}`,
        kind: 'item' as const,
        children: (edu.highlights || []).slice(0, 2).map((h, i) => ({
          id: `edu-${edu.id}-${i}`,
          label: h.length > 24 ? `${h.slice(0, 22)}…` : h.toLowerCase(),
          sub: 'highlight',
          kind: 'metric' as const,
        })),
      })),
    },
    {
      id: 'projects',
      label: 'projects',
      kind: 'branch',
      action: { type: 'view', view: 'projects' },
      children: PROJECTS.map((p) => ({
        id: `proj-${p.id}`,
        label: p.name,
        sub: p.cluster,
        kind: 'item' as const,
        action: { type: 'project' as const, id: p.id },
        children: p.metrics.map(([v, k]) => ({
          id: `proj-${p.id}-${k}`,
          label: v,
          sub: k,
          kind: 'metric' as const,
        })),
      })),
    },
    {
      id: 'research',
      label: 'research',
      kind: 'branch',
      action: { type: 'view', view: 'research' },
      children: PUBS.map((p, i) => ({
        id: `pub-${i}`,
        label: i === 0 ? 'csct 2023 · sentiment' : 'speculative decoding',
        sub: String(p.year),
        kind: 'item' as const,
        children: [
          {
            id: `pub-${i}-venue`,
            label: p.year === 2023 ? 'springer' : 'in progress',
            sub: 'venue',
            kind: 'metric' as const,
          },
        ],
      })),
    },
    {
      id: 'blogs',
      label: 'blogs',
      kind: 'branch',
      action: { type: 'view', view: 'blogs' },
      children: BLOGS.map((b) => ({
        id: `blog-${b.id}`,
        label: b.title.length > 28 ? `${b.title.slice(0, 26)}…` : b.title.toLowerCase(),
        sub: b.date,
        kind: 'item' as const,
        action: { type: 'link' as const, href: b.url },
        children: b.tags.slice(0, 2).map((t) => ({
          id: `blog-${b.id}-${t}`,
          label: t,
          sub: 'tag',
          kind: 'metric' as const,
        })),
      })),
    },
    {
      id: 'competitions',
      label: 'competitions',
      kind: 'branch',
      children: [
        {
          id: 'comp-food',
          label: 'ai & food insecurity',
          sub: 'capital area food bank hackathon',
          kind: 'item',
          children: [
            { id: 'comp-food-1', label: 'hackathon', sub: 'entered', kind: 'metric' },
          ],
        },
        {
          id: 'comp-count',
          label: '5+',
          sub: 'hackathons entered',
          kind: 'item',
        },
      ],
    },
    {
      id: 'skills',
      label: 'skills',
      kind: 'branch',
      children: SKILLS.map((group) => ({
        id: `skill-group-${group.level}`,
        label: group.level,
        sub: group.blurb,
        kind: 'item' as const,
        children: group.items.map((n) => ({
          id: `skill-${group.level}-${n}`,
          label: n,
          sub: group.level,
          kind: 'metric' as const,
        })),
      })),
    },
    {
      id: 'contact',
      label: 'contact',
      kind: 'branch',
      action: { type: 'view', view: 'about' },
      children: [
        {
          id: 'c-email',
          label: 'email',
          sub: CONTACT.email,
          kind: 'item',
          action: { type: 'link', href: `mailto:${CONTACT.email}` },
        },
        {
          id: 'c-github',
          label: 'github',
          sub: 'protocorn',
          kind: 'item',
          action: { type: 'link', href: CONTACT.github },
        },
        {
          id: 'c-linkedin',
          label: 'linkedin',
          sub: 'sahil chordia',
          kind: 'item',
          action: { type: 'link', href: CONTACT.linkedin },
        },
        {
          id: 'c-resume',
          label: 'resume',
          sub: 'pdf',
          kind: 'item',
          action: { type: 'link', href: CONTACT.resume },
        },
      ],
    },
  ],
};

export function findNode(root: GraphNode, id: string): GraphNode | null {
  if (root.id === id) return root;
  for (const c of root.children || []) {
    const f = findNode(c, id);
    if (f) return f;
  }
  return null;
}

export function getPathIds(root: GraphNode, id: string, path: string[] = []): string[] | null {
  const next = [...path, root.id];
  if (root.id === id) return next;
  for (const c of root.children || []) {
    const f = getPathIds(c, id, next);
    if (f) return f;
  }
  return null;
}

export interface FlatNode {
  id: string;
  label: string;
  sub?: string;
  kind: GraphKind;
  depth: number;
  action?: GraphAction;
}

export interface FlatLink {
  source: string;
  target: string;
  depth: number; // target depth
}

export interface FlatGraph {
  nodes: FlatNode[];
  links: FlatLink[];
}

const KIND_DEPTH: Record<GraphKind, number> = {
  root: 0,
  branch: 1,
  item: 2,
  metric: 3,
};

/** Flatten tree into Neo4j-style nodes + relationships with LOD depth. */
export function flattenGraph(root: GraphNode = GRAPH_ROOT): FlatGraph {
  const nodes: FlatNode[] = [];
  const links: FlatLink[] = [];

  const walk = (node: GraphNode, parentId: string | null) => {
    const depth = KIND_DEPTH[node.kind];
    nodes.push({
      id: node.id,
      label: node.label,
      sub: node.sub,
      kind: node.kind,
      depth,
      action: node.action,
    });
    if (parentId) {
      links.push({ source: parentId, target: node.id, depth });
    }
    node.children?.forEach((c) => walk(c, node.id));
  };

  walk(root, null);
  return { nodes, links };
}

/** Map camera zoom (k) → max visible depth (0..3). Zoom in = more detail. */
export function depthFromZoom(k: number): number {
  if (k < 0.55) return 1; // me + branches
  if (k < 1.15) return 2; // + items
  return 3; // + metrics
}
